/**
 * Componente ToastNotification: https://react-bootstrap.netlify.app/docs/components/toasts/
 * Muestra una notificación emergente (toast) utilizando la librería React-Bootstrap. 
 *
 * @param {Object} props - Propiedades del componente.
 * @param {string} [props.title='Notificación'] - Título que se mostrará en el encabezado del toast.
 * @param {string} props.message - Mensaje principal que se mostrará en el cuerpo del toast.
 * @param {number} [props.duration=5000] - Duración en milisegundos que la notificación permanecerá visible antes de ocultarse automáticamente.
 * @param {boolean} props.show - Controla si el toast debe mostrarse o no.
 * @param {Function} [props.onClose] - Función que se ejecutará cuando el toast se cierre.
 * @param {string} [props.position='middle-center'] - Posición en la pantalla donde se mostrará el toast. 
 *        Valores posibles: 'top-start', 'top-center', 'top-end', 'middle-start', 'middle-center', 
 *        'middle-end', 'bottom-start', 'bottom-center', 'bottom-end'.
 * @param {string} [props.variations='light'] - Variación del tema del toast. Valores posibles: 
 *        'primary', 'secondary', 'success', 'danger', 'warning', 'info', 'light', 'dark'.
 * />
 */
import React, { useEffect } from 'react';
import { Toast, ToastContainer } from 'react-bootstrap';
import { useToast } from '../../context/ToastContext';

const ToastNotification = () => {
  const { toasts, closeToast } = useToast();  // Usamos el hook aquí

  useEffect(() => {
    toasts.forEach((toast) => {
      if (toast.show) {
        const timeout = setTimeout(() => {
          closeToast(toast.id); // Eliminar el toast cuando se acabe el tiempo
        }, toast.duration);

        return () => clearTimeout(timeout); // Limpiar el timeout si el componente se desmonta
      }
    });
  }, [toasts, closeToast]);

  return (
    <ToastContainer
      position="top-end" // Posición de los toasts global - por si hay más de uno
      className="p-3"
      style={{ zIndex: 99999 }} // Asegura que los toasts no se solapen con otros elementos
    >
      {toasts.map((toast) => (
        <Toast
          key={toast.id+toast.title}
          show={toast.show}
          onClose={() => closeToast(toast.id)}
          delay={toast.duration}
          autohide
          bg={toast.variations}
          style={{ marginBottom: '10px' }} // Agregar margen entre los toasts
          onClick={() => closeToast(toast.id)} // Cerrar al hacer clic
        >
          <Toast.Header>
            <strong className="me-auto">{toast.title}</strong>
          </Toast.Header>
          <Toast.Body>{toast.message}</Toast.Body>
        </Toast>
      ))}
    </ToastContainer>
  );
};

export default ToastNotification;
